import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type MutableRefObject,
    type ReactNode,
    type RefObject,
} from "react";
import { useMediaQuery } from "react-responsive";
import type { SideNavMode } from "./SideNav.types";
import {
    readStoredSideNavState,
    writeStoredSideNavState,
    type StoredSideNavState,
} from "./sideNavStorage";
import { useSideNavFocusReturn } from "./useSideNavFocusReturn";
import {
    clampOpenWidth,
    DEFAULT_OPEN_WIDTH,
    MAX_OPEN_WIDTH,
} from "./sideNavWidth";

const NARROW_VIEWPORT_QUERY = "(max-width: 799px)";

interface SideNavState {
    /** Open (`width`) or mini (56px); below 800px mini unless switched for the session. */
    mode: SideNavMode;
    setMode: (mode: SideNavMode) => void;
    toggleMode: () => void;
    /** The open width, 180–400px (220px by default), kept while mini or hidden; below 800px 220px unless resized for the session. */
    width: number;
    setWidth: (width: number) => void;
    /** Hiding keeps the mode, so showing returns to it. */
    isHidden: boolean;
    setHidden: (isHidden: boolean) => void;
    toggle: () => void;
}

interface TemporarySideNavMode {
    mode: SideNavMode;
    /** Remembers mode changes under this view's name, and starts from the remembered mode instead of `mode`; not below 800px. */
    rememberAs?: string;
}

interface SideNavContextValue extends Omit<SideNavState, "width"> {
    /** Hides the nav without saving it (e.g. a fullscreen view); show and toggle are off until released. */
    isSuppressed: boolean;
    setSuppressed: (isSuppressed: boolean) => void;
    /** Overrides the saved mode (e.g. mini while a task is open) until released with null; mode changes meanwhile are saved only under `rememberAs`. */
    temporaryMode: SideNavMode | null;
    setTemporaryMode: (temporary: TemporarySideNavMode | null) => void;
    /** Attached to the `SideNav` and `SideNavShowButton` elements, to return focus on hide. */
    navRef: RefObject<HTMLElement | null>;
    showButtonRef: RefObject<HTMLButtonElement | null>;
    /** Row state kept by `useSideNavMemory` across the nav's remounts, for this provider's lifetime. */
    memoryRef: MutableRefObject<Map<string, boolean>>;
}

const SideNavContext = createContext<SideNavContextValue | null>(null);
// Apart from the rest, so a resize re-renders only what shows the width, not every row.
const SideNavWidthContext = createContext(DEFAULT_OPEN_WIDTH);

/**
 * Below 800px the nav is mini and 220px open whatever is saved; it can be
 * switched to open and resized there for the session, until the viewport
 * crosses 800px again.
 */
const useNarrowViewportMode = () => {
    const isNarrowViewport = useMediaQuery({ query: NARROW_VIEWPORT_QUERY });
    const [narrowMode, setNarrowMode] = useState<SideNavMode>("mini");
    const [narrowWidth, setNarrowWidth] = useState(DEFAULT_OPEN_WIDTH);
    const [wasNarrowViewport, setWasNarrowViewport] =
        useState(isNarrowViewport);
    if (wasNarrowViewport !== isNarrowViewport) {
        setWasNarrowViewport(isNarrowViewport);
        setNarrowMode("mini");
        setNarrowWidth(DEFAULT_OPEN_WIDTH);
    }
    return {
        isNarrowViewport,
        narrowMode,
        setNarrowMode,
        narrowWidth,
        setNarrowWidth,
    };
};

/**
 * Publishes the open width as `--side-nav-width` on the root, where the
 * full-width overlays that start at the nav's edge read it. A provider
 * nested in another (the design system's sandbox) leaves it to the outer one.
 */
const usePublishedWidth = (width: number) => {
    const isOutermost = useContext(SideNavContext) === null;
    useLayoutEffect(() => {
        if (!isOutermost) {
            return undefined;
        }
        const root = document.documentElement;
        root.style.setProperty("--side-nav-width", `${width}px`);
        return () => {
            root.style.removeProperty("--side-nav-width");
        };
    }, [isOutermost, width]);
};

/**
 * Shared state of one `SideNav`: open, mini or hidden, and the open width.
 * Wrap the whole app shell so the page header's `SideNavShowButton` and the
 * nav agree. Mode, hidden and width persist in localStorage under
 * `storageKey`; `legacyHiddenStorageKey` is read once when nothing is saved
 * there yet.
 */
export const SideNavProvider = ({
    storageKey,
    legacyHiddenStorageKey,
    children,
}: {
    storageKey: string;
    legacyHiddenStorageKey?: string;
    children: ReactNode;
}) => {
    const [stored, setStored] = useState(() =>
        readStoredSideNavState({ storageKey, legacyHiddenStorageKey }),
    );
    // The latest save, so two setters called in one handler both land.
    const latestStoredRef = useRef(stored);
    const {
        isNarrowViewport,
        narrowMode,
        setNarrowMode,
        narrowWidth,
        setNarrowWidth,
    } = useNarrowViewportMode();
    const [isSuppressed, setIsSuppressed] = useState(false);
    const [temporary, setTemporary] = useState<TemporarySideNavMode | null>(
        null,
    );
    const temporaryMode = temporary?.mode ?? null;
    const memoryRef = useRef(new Map<string, boolean>());
    const mode = temporaryMode ?? (isNarrowViewport ? narrowMode : stored.mode);
    const isHidden = isSuppressed || stored.isHidden;
    const width = isNarrowViewport
        ? narrowWidth
        : clampOpenWidth({
              width: stored.width ?? DEFAULT_OPEN_WIDTH,
              max: MAX_OPEN_WIDTH,
          });
    usePublishedWidth(width);
    const {
        navRef,
        showButtonRef,
        noteHiding,
        noteShowing,
        returnFocus,
        focusNav,
    } = useSideNavFocusReturn();

    useEffect(() => {
        if (isHidden) {
            returnFocus();
        } else {
            focusNav();
        }
    }, [isHidden, returnFocus, focusNav]);

    const save = useCallback(
        (patch: Partial<StoredSideNavState>) => {
            const state = { ...latestStoredRef.current, ...patch };
            latestStoredRef.current = state;
            setStored(state);
            writeStoredSideNavState({ storageKey, state });
        },
        [storageKey],
    );

    const setHidden = useCallback(
        (nextIsHidden: boolean) => {
            if (isSuppressed) {
                return;
            }
            if (nextIsHidden) {
                noteHiding();
            } else {
                noteShowing();
            }
            save({ isHidden: nextIsHidden });
        },
        [isSuppressed, noteHiding, noteShowing, save],
    );
    const toggle = useCallback(
        () => setHidden(!isHidden),
        [isHidden, setHidden],
    );

    const setTemporaryMode = useCallback(
        (next: TemporarySideNavMode | null) => {
            if (next?.rememberAs === undefined || isNarrowViewport) {
                setTemporary(next);
                return;
            }
            const rememberedModes = latestStoredRef.current.rememberedModes;
            setTemporary({
                ...next,
                mode: rememberedModes?.[next.rememberAs] ?? next.mode,
            });
        },
        [isNarrowViewport],
    );

    const setMode = useCallback(
        (nextMode: SideNavMode) => {
            if (temporary !== null) {
                setTemporary({ ...temporary, mode: nextMode });
                if (temporary.rememberAs !== undefined && !isNarrowViewport) {
                    save({
                        rememberedModes: {
                            ...latestStoredRef.current.rememberedModes,
                            [temporary.rememberAs]: nextMode,
                        },
                    });
                }
                return;
            }
            if (isNarrowViewport) {
                setNarrowMode(nextMode);
                return;
            }
            save({ mode: nextMode });
        },
        [temporary, isNarrowViewport, setNarrowMode, save],
    );
    const toggleMode = useCallback(
        () => setMode(mode === "open" ? "mini" : "open"),
        [mode, setMode],
    );
    const setWidth = useCallback(
        (nextWidth: number) => {
            if (isNarrowViewport) {
                setNarrowWidth(
                    clampOpenWidth({ width: nextWidth, max: MAX_OPEN_WIDTH }),
                );
                return;
            }
            save({ width: nextWidth });
        },
        [isNarrowViewport, setNarrowWidth, save],
    );

    const value = useMemo(
        () => ({
            mode,
            setMode,
            toggleMode,
            setWidth,
            isHidden,
            setHidden,
            toggle,
            isSuppressed,
            setSuppressed: setIsSuppressed,
            temporaryMode,
            setTemporaryMode,
            navRef,
            showButtonRef,
            memoryRef,
        }),
        [
            mode,
            setMode,
            toggleMode,
            setWidth,
            isHidden,
            setHidden,
            toggle,
            isSuppressed,
            temporaryMode,
            setTemporaryMode,
            navRef,
            showButtonRef,
            memoryRef,
        ],
    );

    return (
        <SideNavContext.Provider value={value}>
            <SideNavWidthContext.Provider value={width}>
                {children}
            </SideNavWidthContext.Provider>
        </SideNavContext.Provider>
    );
};

/** The nearest provider's full state, or null for a static nav (playground plates). */
export const useOptionalSideNavContext = () => useContext(SideNavContext);

export const useSideNavContext = () => {
    const context = useContext(SideNavContext);
    if (context === null) {
        throw new Error("useSideNav must be used inside a SideNavProvider");
    }
    return context;
};

/** The surrounding provider's open width; read apart from `useSideNavContext`, which leaves it out. */
export const useSideNavWidth = () => useContext(SideNavWidthContext);

/** Mode, width and hidden state of the surrounding `SideNavProvider`. */
export const useSideNav = (): SideNavState => {
    const context = useSideNavContext();
    const width = useSideNavWidth();
    return { ...context, width };
};

/**
 * Puts the nearest provider's nav in `mode` while it is non-null and this
 * component is mounted, without changing the saved mode; null or unmount
 * returns to it. With `rememberAs`, a mode the user switches to meanwhile is
 * saved under that name and used instead of `mode` next time (from 800px up).
 * No-op outside a provider.
 */
export const useTemporarySideNavMode = ({
    mode,
    rememberAs,
}: {
    mode: SideNavMode | null;
    rememberAs?: string;
}) => {
    const setTemporaryMode = useOptionalSideNavContext()?.setTemporaryMode;

    useEffect(() => {
        if (!setTemporaryMode || mode === null) {
            return;
        }
        setTemporaryMode({ mode, rememberAs });
        return () => setTemporaryMode(null);
    }, [mode, rememberAs, setTemporaryMode]);
};

/** The nearest provider's mode; open for a static nav. */
export const useSideNavMode = (): SideNavMode =>
    useOptionalSideNavContext()?.mode ?? "open";
